import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Loader2,
  Hash,
  Mail,
  Search,
  CheckCircle2,
  Clock,
  CalendarClock,
  Trophy,
  XCircle,
  Sparkles,
  ArrowLeft,
} from "lucide-react";
import { useEmployerBrand, BrandLogo, PoweredByBadge } from "@/lib/brand";

const H_LOGO_URL =
  "/brand/hireloop-h.png";

// Visual stages shown in the timeline (excludes the terminal "not_interested").
const STAGES = [
  { key: "new", label: "Application received", Icon: CheckCircle2 },
  { key: "awaiting_response", label: "Awaiting your response", Icon: Clock },
  { key: "interview_scheduled", label: "Interview scheduled", Icon: CalendarClock },
  { key: "hired", label: "Hired", Icon: Trophy },
];

const STAGE_INDEX = {
  new: 0,
  awaiting_response: 1,
  interview_scheduled: 2,
  hired: 3,
  not_hired: -1,
  not_interested: -1,
};

const STATUS_LABELS = {
  new: "Application received",
  awaiting_response: "Awaiting your response",
  interview_scheduled: "Interview scheduled",
  hired: "Hired",
  not_hired: "Closed — not moving forward",
  not_interested: "Closed — withdrawn",
};

const formatReceived = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(
    d.getDate()
  ).padStart(2, "0")}/${d.getFullYear()}`;
};

export default function ApplicationStatus() {
  const { employerId } = useParams();
  const { brand } = useEmployerBrand(employerId);
  const [ref, setRef] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!ref.trim() || !email.trim()) {
      toast.error("Please enter both the reference number and email");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/public/applications/status", {
        application_ref: ref.trim().toUpperCase(),
        email: email.trim().toLowerCase(),
      });
      setResult(data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Could not look up that application — please double-check the reference and email.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError("");
    setRef("");
    setEmail("");
  };

  return (
    <main
      data-testid="application-status-page"
      className="mx-auto max-w-2xl px-4 py-6 sm:px-6 sm:py-12"
    >
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to home
      </Link>

      <header className="mt-6 flex items-center gap-3">
        {brand?.logo_url ? (
          <BrandLogo brand={brand} className="h-12 w-auto max-w-[160px] object-contain" />
        ) : (
          !brand?.hide_powered_by && (
            <img
              src={H_LOGO_URL}
              alt="Hireloop PRO"
              className="h-10 w-10 object-contain"
              style={{ mixBlendMode: "multiply" }}
            />
          )
        )}
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-blue-700">
          Application status{brand?.company ? ` · ${brand.company}` : ""}
        </div>
      </header>

      <h1 className="mt-6 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
        Check your application
      </h1>
      <p className="mt-3 text-sm text-black">
        Enter the reference number you received when you submitted your résumé,
        plus the email you used. We'll show you where things stand.
      </p>

      {!result ? (
        <form
          onSubmit={submit}
          className="mt-8 space-y-5 border border-slate-200 bg-white p-6"
        >
          <div>
            <Label
              htmlFor="status-ref"
              className="text-xs font-bold uppercase tracking-[0.2em] text-black"
            >
              <Hash className="mr-1 inline h-3 w-3 -translate-y-0.5" />
              Application reference
            </Label>
            <Input
              id="status-ref"
              data-testid="status-ref-input"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              autoComplete="off"
              className="mt-2 rounded-sm border-slate-300 font-mono uppercase"
              placeholder="APP-2026-00001"
            />
          </div>
          <div>
            <Label
              htmlFor="status-email"
              className="text-xs font-bold uppercase tracking-[0.2em] text-black"
            >
              <Mail className="mr-1 inline h-3 w-3 -translate-y-0.5" />
              Email used in your application
            </Label>
            <Input
              id="status-email"
              data-testid="status-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className="mt-2 rounded-sm border-slate-300"
              placeholder="you@example.com"
            />
          </div>

          {error && (
            <div
              data-testid="status-error"
              className="border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid="status-submit-btn"
            disabled={loading}
            className="w-full rounded-sm bg-blue-700 py-6 hover:bg-blue-600"
          >
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Search className="mr-2 h-4 w-4" />
            )}
            Look up my application
          </Button>
        </form>
      ) : (
        <ResultCard result={result} onReset={reset} brand={brand} />
      )}
      <div className="mt-10 flex justify-end">
        <PoweredByBadge brand={brand} />
      </div>
    </main>
  );
}

function ResultCard({ result, onReset, brand }) {
  const isClosed =
    result.application_status === "not_interested" ||
    result.application_status === "not_hired";
  const isWithdrawn = result.application_status === "not_interested";
  const activeIdx = STAGE_INDEX[result.application_status] ?? 0;
  const label =
    STATUS_LABELS[result.application_status] || "Application received";

  return (
    <section
      data-testid="status-result-card"
      className={`mt-8 border px-6 py-7 ${
        isClosed
          ? "border-slate-300 bg-slate-50"
          : "border-emerald-200 bg-emerald-50"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div
            className={`text-[10px] font-bold uppercase tracking-[0.22em] ${
              isClosed ? "text-slate-500" : "text-emerald-700"
            }`}
          >
            <Hash className="mr-1 inline h-3 w-3 -translate-y-0.5" />
            {result.application_ref}
          </div>
          <h2
            data-testid="status-result-headline"
            className={`mt-1 font-heading text-2xl font-semibold ${
              isClosed ? "text-slate-900" : "text-emerald-950"
            }`}
          >
            {label}
          </h2>
          <p
            className={`mt-1 text-xs ${
              isClosed ? "text-slate-600" : "text-emerald-800"
            }`}
          >
            {result.position ? `${result.position} · ` : ""}
            {result.employer_label}
            {result.received_at
              ? ` · Received ${formatReceived(result.received_at)}`
              : ""}
          </p>
        </div>
        <button
          data-testid="status-reset-btn"
          type="button"
          onClick={onReset}
          className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
        >
          Check another
        </button>
      </div>

      {isClosed ? (
        <div className="mt-6 flex items-start gap-3 border-t border-slate-200 pt-5 text-sm text-slate-700">
          <XCircle className="mt-0.5 h-5 w-5 flex-none text-slate-500" />
          <div>
            <div className="font-semibold text-slate-900">
              {isWithdrawn
                ? "This application has been withdrawn."
                : "We've moved forward with other candidates."}
            </div>
            <div className="mt-1 text-xs text-slate-600">
              {isWithdrawn
                ? `Thanks for letting us know — we'll keep your information on file at ${result.employer_label} for future roles.`
                : `Thanks for taking the time to apply — we'll keep your information on file for future roles at ${result.employer_label}.`}
            </div>
          </div>
        </div>
      ) : (
        <ol
          data-testid="status-timeline"
          className="mt-6 space-y-3 border-t border-emerald-200 pt-5"
        >
          {STAGES.map((s, idx) => {
            const isDone = idx < activeIdx;
            const isCurrent = idx === activeIdx;
            const Icon = s.Icon;
            return (
              <li
                key={s.key}
                data-testid={`status-stage-${s.key}`}
                className="flex items-start gap-3 text-sm"
              >
                <div
                  className={`mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full border ${
                    isDone
                      ? "border-emerald-500 bg-emerald-500 text-white"
                      : isCurrent
                      ? "border-emerald-600 bg-white text-emerald-700"
                      : "border-slate-300 bg-white text-slate-400"
                  }`}
                >
                  <Icon className="h-3 w-3" />
                </div>
                <div className="flex-1">
                  <div
                    className={`font-semibold ${
                      isDone || isCurrent ? "text-emerald-950" : "text-slate-500"
                    }`}
                  >
                    {s.label}
                    {isCurrent && (
                      <span className="ml-2 inline-flex items-center rounded-sm bg-emerald-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-white">
                        You are here
                      </span>
                    )}
                  </div>
                  {isCurrent && s.key === "awaiting_response" && (
                    <div className="mt-1.5 text-xs text-emerald-800">
                      <div>
                        The team invited you to complete an AI interview
                        {result.interview_invited_at
                          ? ` on ${formatReceived(result.interview_invited_at)}`
                          : ""}
                        . Check your inbox for the link.
                      </div>
                      {result.interview_id && (
                        <a
                          href={`${window.location.origin}/interview/session/${result.interview_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="status-resume-interview-btn"
                          className="mt-2 inline-flex items-center gap-1.5 border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white hover:bg-emerald-500"
                        >
                          Resume AI interview →
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {!brand?.hide_powered_by && (
        <p
          className={`mt-6 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] ${
            isClosed ? "text-slate-500" : "text-emerald-700"
          }`}
        >
          <Sparkles className="h-3 w-3" /> Reviewed with Hireloop PRO
        </p>
      )}
    </section>
  );
}